import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import EmailOtpVerification from "../components/EmailOtpVerification";

function EmailOtpVerificationSignup() {
  const location = useLocation<any>();
  const history = useHistory<any>();

  const urlSearchParams = new URLSearchParams(location.search);
  const emailParam = urlSearchParams.get('email');
  const redirectUrl = urlSearchParams.get('redirect_to');
  const oauthFlow = urlSearchParams.get('oauth_flow');
  const isOAuthFlow = oauthFlow === '1' || oauthFlow === 'true';
  const currentSearch = location.search;
  const { state } = location;
  const emailFromState = state?.email;

  const [email, setEmail] = useState<string>("");
  const [autoSendOtp, setAutoSendOtp] = useState(false);
  const [otpAlreadySent, setOtpAlreadySent] = useState(false);

  useEffect(() => {
    // Get email from state (from signup redirect) or query params
    const emailToUse = emailFromState || emailParam;
    if (emailToUse) {
      setEmail(emailToUse);
      // If email comes from signup state, OTP was already sent during signup
      // Show OTP input fields directly without "Send OTP" button
      if (emailFromState) {
        setOtpAlreadySent(true);
        setAutoSendOtp(false);
        // Start the timer for resend (default 1 minute)
        // We don't set initialDelay here, just mark as already sent
      } else if (emailParam) {
        // Email from query params - auto-send OTP
        setAutoSendOtp(true);
        setOtpAlreadySent(false);
      }
    }
  }, [emailParam, emailFromState]);

  const handleVerifySuccess = (response: any) => {
    // After OTP verification, email is verified and user can login
    // If refresh_token is returned, user is already logged in
    if (response?.data?.refresh_token) {
      // User is logged in, redirect to dashboard
      if (redirectUrl) {
        try {
          const redirectTo = new URL(redirectUrl, window.location.origin);
          window.location.href = redirectTo.href;
        } catch (e) {
          history.push({
            pathname: "/",
            state: { response: true, message: "Account verified and logged in successfully" },
          });
        }
      } else {
        history.push({
          pathname: "/",
          state: { response: true, message: "Account verified and logged in successfully" },
        });
      }
    } else {
      // Email verified but need to login
      history.push({
        pathname: "/login",
        search: currentSearch,
        state: {
          response: true,
          message: "Email verified successfully. Please login to continue.",
          verifiedEmail: email
        },
      });
    }
  };

  if (!email) {
    return null;
  }

  return (
    <PageShell>
      <Card>
        <RightBanner $isOAuthFlow={isOAuthFlow}>
          <Heading $isOAuthFlow={isOAuthFlow}>
            {isOAuthFlow ? "Verify OTP" : "Verify Your Email OTP"}
          </Heading>
          <OTPContainer>
            <EmailOtpVerification
              email={email}
              mode="signup"
              autoSendOtp={autoSendOtp}
              otpAlreadySent={otpAlreadySent}
              isOAuthFlow={isOAuthFlow}
              redirectUrl={redirectUrl}
              onVerifySuccess={handleVerifySuccess}
              onEmailChange={(newEmail) => setEmail(newEmail)}
            />
          </OTPContainer>
        </RightBanner>
      </Card>
    </PageShell>
  );
}

export default EmailOtpVerificationSignup;

/* These routes render outside AuthScreenContainer, so the page carries its
   own centered shell using the same visual tokens as the auth card. */
const PageShell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f5f7fa 100%);
  font-family: "Poppins", sans-serif;
  @media (max-width: 576px) {
    padding: 16px 12px;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
`;

const RightBanner = styled.div<{ $isOAuthFlow?: boolean }>`
  padding: 32px 40px;
  width: 100%;
  @media (max-width: 576px) {
    padding: 20px 16px;
  }
`;

const Heading = styled.h1<{ $isOAuthFlow?: boolean }>`
  font-weight: 700;
  font-size: 24px;
  line-height: 36px;
  text-align: center;
  color: ${props => props.$isOAuthFlow ? '#333333' : '#073b5f'};
  @media (max-width: 576px) {
    font-size: 20px;
  }
`;

const OTPContainer = styled.div`
  width: 100%;
  margin: 24px auto 0;
`;